import React from 'react';
import { useRecoilState } from 'recoil';
import { useUpdateEffect } from 'react-use';
import { LineItem } from 'app/components/Charts/line/data';
import { Box, Grid, Typography } from '@material-ui/core';
import { DetailProjectsFragment } from 'app/fragments/detail-projects-fragment';
import { SwitchMapState, lineYearPeriodFilter } from 'app/state/recoil/atoms';
import {
  LinkContainerStyle,
  SubTextStyle,
  WikipediaLinkStyle,
} from 'app/fragments/overview-fragment/style';
import { OverviewStatItemProps } from 'app/fragments/overview-fragment/common/OverviewStatItem';
import { Launch } from '@material-ui/icons';
import { DatalessLinechart } from 'app/components/Charts/line/DatalessLinechart';
import { BarChart } from 'app/components/Charts/bar/BarChart';

interface OverviewFragmentLayoutProps {
  title: string;
  subText: string;
  wikiLink?: string;
  lineCount: number;
  lineData: LineItem;
  statData: OverviewStatItemProps[];
  detailBaseFilter: string;
  pageParam: string;
  type: string;
}

export const OverviewFragmentLayout = (props: OverviewFragmentLayoutProps) => {
  const [isViz] = useRecoilState(SwitchMapState);
  const setYearPeriod = useRecoilState(lineYearPeriodFilter)[1];
  const isCountryView = props.type === 'country';

  function renderBarChart() {
    return (
      <div
        css={`
          display: flex;
          flex-direction: column;
        `}
      >
        {isCountryView && <Box height="100%" width="38px" />}
        {props.lineData.data.length > 0 ? (
          <BarChart
            data={props.lineData.data}
            indexBy="x"
            keys={['x', 'y']}
            aria-label="Bar chart showing total activity budget amount in US dollars per year."
          />
        ) : (
          <DatalessLinechart />
        )}
      </div>
    );
  }

  useUpdateEffect(() => {
    if (isViz) {
      setYearPeriod('');
    }
  }, [isViz]);

  return (
    <Grid
      container
      item
      lg={12}
      css={`
        margin-top: ${isViz ? '48px' : 0};
        @media (max-width: 960px) {
          gap: 20px;
        }
      `}
    >
      {isViz && isCountryView && props.subText && (
        <Grid
          item
          sm={12}
          md={12}
          lg={4}
          css={`
            @media (max-width: 960px) {
              order: 2;
            }
          `}
        >
          {/* ----------------------------------------------------------------- */}
          {/* fragment description */}
          <Typography css={SubTextStyle}>{props.subText}</Typography>
          {props.wikiLink && (
            <div css={LinkContainerStyle}>
              <a
                target="_blank"
                href={props.wikiLink}
                css={WikipediaLinkStyle}
                rel="noreferrer"
              >
                Continue Reading on Wikipedia <Launch />
              </a>
            </div>
          )}
        </Grid>
      )}
      <Grid
        item
        sm={12}
        md={12}
        lg={isViz && isCountryView && props.subText ? 8 : 12}
        css={`
          @media (max-width: 960px) {
            order: 1;
          }
        `}
      >
        {isViz ? (
          renderBarChart()
        ) : (
          <DetailProjectsFragment
            includeYearPeriodFilter
            pageTitle={props.title}
            pageParam={props.pageParam}
            detailBaseFilter={props.detailBaseFilter}
            statData={props.statData}
          />
        )}
      </Grid>
    </Grid>
  );
};
